import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { CreateFulfillmentOrder } from "@redotech/redo-fulfillment-operations-common/create-fulfillment-order";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { memo, useMemo } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { LocationContext } from "../services/location-service";
import {
  manualOrderForm,
  ManualOrderFormValue,
  ManualOrderSlideout,
} from "./manual-order-form";

export const EditManualOrderSlideout = memo(function EditManualOrderSlideout({
  fulfillmentGroupData,
  onClose,
  open,
  triggerRefresh,
}: {
  fulfillmentGroupData: FulfillmentGroupData;
  onClose: () => void;
  open: boolean;
  triggerRefresh: () => void;
}) {
  const [locationsLoad, _] = useLazyContext(LocationContext);

  const order: ManualOrderFormValue = useMemo<ManualOrderFormValue>(() => {
    const addressToUse = fulfillmentGroupData.destinationAddress;

    const address = {
      name: addressToUse?.name ?? "",
      address1: addressToUse?.address1 ?? "",
      address2: addressToUse?.address2 ?? "",
      city: addressToUse?.city ?? "",
      company: addressToUse?.company ?? "",
      countryCode: addressToUse?.countryCode ?? RedoShopifyCountryCode.Us,
      email: addressToUse?.email ?? "",
      phone: addressToUse?.phone ?? "",
      province: addressToUse?.province ?? "",
      zip: addressToUse?.zip ?? "",
    };

    const originAddress = fulfillmentGroupData.originAddress;
    const location = locationsLoad.value?.find(
      (l) => l.externalId === originAddress.locationId,
    );

    // A one-off (non-location) origin has no matching saved location; reopen it
    // in custom mode so the ship-from address survives edits.
    const customLocation = location
      ? undefined
      : {
          name: originAddress.name ?? "",
          address1: originAddress.address1 ?? "",
          address2: originAddress.address2 ?? "",
          city: originAddress.city ?? "",
          company: originAddress.company ?? "",
          countryCode: originAddress.countryCode ?? RedoShopifyCountryCode.Us,
          email: originAddress.email ?? "",
          phone: originAddress.phone ?? "",
          province: originAddress.province ?? "",
          zip: originAddress.zip ?? "",
        };

    const orderInfo: ManualOrderFormValue = {
      location: location ?? null,
      customLocation,
      address,
      orderInfo: {
        orderNumber: fulfillmentGroupData.orders[0]?.name ?? "",
        orderDate: fulfillmentGroupData.orderCreatedAt ?? new Date(),
        paidDate: new Date(), // To my knowledge this isn't saved anywhere
        shippingPaid: {
          amount: fulfillmentGroupData.totalShippingCost.amount,
          currency: fulfillmentGroupData.totalShippingCost.currency,
        },
        taxPaid: {
          amount: fulfillmentGroupData.totalTax.amount,
          currency: fulfillmentGroupData.totalTax.currency,
        },
        totalPaid: {
          amount: fulfillmentGroupData.totalPrice.amount,
          currency: fulfillmentGroupData.totalPrice.currency,
        },
        items: fulfillmentGroupData.items,
        internalNote: fulfillmentGroupData.internalNote ?? "",
      },
      fulfillBy: null,
    };

    return orderInfo;
  }, [fulfillmentGroupData, locationsLoad.value]);

  const form = useInput(manualOrderForm, order);

  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const rpcCall = useHandler(
    async (
      createFulfillmentOrder: CreateFulfillmentOrder,
    ): Promise<{
      success: boolean;
      fulfillmentGroups: FulfillmentGroupData[];
    }> => {
      const result = await rpcClient.editManualFulfillmentGroups({
        fulfillmentGroupEdits: [
          {
            fulfillmentGroupId: fulfillmentGroupData._id,
            data: createFulfillmentOrder,
          },
        ],
      });

      return {
        success: result.success,
        fulfillmentGroups: [fulfillmentGroupData],
      };
    },
  );

  return (
    <ManualOrderSlideout
      errorMessage="Failed to update order"
      form={form}
      locations={locationsLoad.value ?? []}
      onClose={onClose}
      onSuccess={triggerRefresh}
      open={open}
      rpcCall={rpcCall}
      submitButtonText="Save changes"
      successMessage="Order updated"
      title="Edit order"
    />
  );
});
